<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Ad Placement Group</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>

      <v-card-text class="resizable-card__body">
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense class="mt-3">
          <v-col cols="12" md="12" sm="12">
            <v-text-field
                disabled
                label="UID:"
                v-model.trim="model.uid"
                hide-details
                outlined
                dense
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
        </v-row>

        <v-row dense style="margin-top: -12px">
          <v-col cols="12" md="4" sm="4">
            <v-text-field label="Ad Unit IOS:" v-model.trim="model.adUnitIOS" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-text-field label="Ad Unit Android:" v-model.trim="model.adUnitAndroid" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-text-field label="Ad Unit Amazon:" v-model.trim="model.adUnitAmazon" hide-details/>
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col cols="12" sm="3" md="3">
            <v-text-field v-model.number="model.cooldown" label="Cooldown:" hide-details/>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-text-field v-model.number="model.dailyLimit" label="Daily Limit:" hide-details/>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-text-field v-model.number="model.sessionLimit" label="Session Limit" hide-details/>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-text-field v-model.number="model.sessionDuration" label="Session Duration" hide-details/>
          </v-col>
        </v-row>

        <v-row dense class="mt-2">
          <v-col cols="12" sm="12" md="12">
            <r-ad-placement-dropdown-multiple :items.sync="model.placements" title="Unit Placements"/>
          </v-col>
        </v-row>

        <v-expansion-panels class="mt-6">
          <r-guard-panel :model.sync="model.guard"/>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>

      <div
          class="resize-handle"
          @mousedown.stop.prevent="startResize"
      ></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {AdUnitModel} from "@/modules/redactor/models/ad-settings/AdUnitModel";

import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel.vue";
import RAdPlacementDropdownMultiple from "@/modules/redactor/views/ad-settings/placement/r-ad-placement-dropdown-multiple.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";


export default {
  name: "r-ad-unit-dialog",
  components: {RPreviewDialog, RAdPlacementDropdownMultiple, RGuardPanel, RAuthorize },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(AdUnitModel),
    rules: ViewRules.READONLY,

    dialogWidth: 690,
    dialogHeight: 546,

    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 690,
    resizeStartHeight: 546,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new AdUnitModel();

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;

      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 500;
      const minHeight = 360;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;

      this.resizing = false;

      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, AdUnitModel)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>